import React from 'react';
import { LuminousBreakLogo } from './LuminousBreakLogo';

interface SupportContactPageProps {
  onNavigateHome?: () => void;
  onNavigatePrivacy?: () => void;
  onNavigateTerms?: () => void;
}

export const SupportContactPage: React.FC<SupportContactPageProps> = ({
  onNavigateHome,
  onNavigatePrivacy,
  onNavigateTerms,
}) => {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FAF8F5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#2D2826',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top Header */}
      <header style={{
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #EAE0D5',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              onClick={onNavigateHome}
              style={{ cursor: onNavigateHome ? 'pointer' : 'default', display: 'flex', alignItems: 'center' }}
            >
              <LuminousBreakLogo size={32} showText={true} />
            </div>
          </div>
          <nav style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px' }}>
            {onNavigateHome ? (
              <button
                onClick={onNavigateHome}
                style={{
                  background: 'none',
                  border: '1px solid #CBD5E1',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  color: '#475569',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                ← Back to Home
              </button>
            ) : (
              <a
                href="/"
                style={{
                  textDecoration: 'none',
                  border: '1px solid #CBD5E1',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  color: '#475569',
                  fontWeight: 500,
                }}
              >
                ← Back to Home
              </a>
            )}
            {onNavigatePrivacy ? (
              <button
                onClick={onNavigatePrivacy}
                style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Privacy Policy
              </button>
            ) : (
              <a href="/privacy" style={{ color: '#2563EB', textDecoration: 'underline' }}>
                Privacy Policy
              </a>
            )}
            {onNavigateTerms ? (
              <button
                onClick={onNavigateTerms}
                style={{ background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Terms of Service
              </button>
            ) : (
              <a href="/terms" style={{ color: '#2563EB', textDecoration: 'underline' }}>
                Terms of Service
              </a>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        flex: 1,
        maxWidth: '900px',
        width: '100%',
        margin: '32px auto',
        padding: '0 20px',
        boxSizing: 'border-box',
      }}>
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.04)',
          padding: '40px',
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 800,
            color: '#1E293B',
            marginTop: 0,
            marginBottom: '8px',
            letterSpacing: '-0.5px',
          }}>
            Luminous Break Support & Help Center
          </h1>
          <p style={{ color: '#64748B', fontSize: '14px', marginTop: 0, marginBottom: '24px' }}>
            We are here to support your mindful digital wellbeing journey. Reach out to our technical, privacy, or clinical operations teams anytime.
          </p>

          {/* Emergency Crisis Notice */}
          <div style={{
            backgroundColor: '#FEF3C7',
            borderRadius: '12px',
            padding: '18px 20px',
            border: '1.5px solid #F59E0B',
            color: '#92400E',
            marginBottom: '32px',
          }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 800, textTransform: 'uppercase' }}>
              ⚠️ Mental Health Emergency & Crisis Notice
            </h4>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6' }}>
              Luminous Break is a mindful habit reflection tool, not an emergency medical service. If you are experiencing a mental health crisis, thoughts of self-harm, or severe medical distress, please immediately call or text <strong>988</strong> (Suicide & Crisis Lifeline in US & Canada), call <strong>911</strong> (US/Canada), <strong>111/999</strong> (UK), or <strong>112</strong> (EU).
            </p>
          </div>

          {/* Official Contact Channels */}
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1E293B', marginBottom: '16px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>
            Official Contact Channels
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '36px' }}>
            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>🛠️</div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0', color: '#0F172A' }}>Technical & Customer Support</h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 8px 0' }}>App issues, device syncing, pause configuration</p>
              <a href="mailto:support@luminousbreak.com" style={{ color: '#2563EB', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
                support@luminousbreak.com
              </a>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>🔒</div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0', color: '#0F172A' }}>Privacy Officer & DPO</h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 8px 0' }}>Data subject requests, zero-knowledge vault inquiries</p>
              <a href="mailto:privacy@luminousbreak.com" style={{ color: '#2563EB', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
                privacy@luminousbreak.com
              </a>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>🩺</div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0', color: '#0F172A' }}>Clinicians & Enterprise</h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 8px 0' }}>Practice licensing, BAA agreements, recipe studio</p>
              <a href="mailto:legal@luminousbreak.com" style={{ color: '#2563EB', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
                legal@luminousbreak.com
              </a>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>💳</div>
              <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px 0', color: '#0F172A' }}>Billing & Inquiries</h3>
              <p style={{ fontSize: '13px', color: '#64748B', margin: '0 0 8px 0' }}>Clinician subscriptions, enterprise invoices</p>
              <a href="mailto:billing@luminousbreak.com" style={{ color: '#2563EB', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
                billing@luminousbreak.com
              </a>
            </div>
          </div>

          {/* Frequently Asked Questions */}
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1E293B', marginBottom: '16px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>
            Frequently Asked Questions
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '32px' }}>
            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
                How do I delete my account and shred my cryptographic vault?
              </h4>
              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#475569' }}>
                You can irrevocably destroy all on-device cryptographic keys, unpair any care team connections, and reset the application at any time directly in the iOS or Android mobile app:
                <br />
                <strong>Settings → Membership & Care Circle → Delete Account & Shred Vault</strong>.
                <br />
                This executes an immediate cryptographic purge on-device and in our cloud databases per Apple Guideline 5.1.1(v).
              </p>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
                Does Luminous Break transmit or view my browsing history?
              </h4>
              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#475569' }}>
                <strong>No. Never.</strong> All trigger evaluations, app package observations, website domain filtering, and reflective journal entries are executed strictly on-device in local RAM. Zero browsing URLs, search queries, or private journal reflections are ever transmitted to or stored on our servers.
              </p>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
                How do I optionally connect with my therapist or healthcare provider?
              </h4>
              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#475569' }}>
                Pairing is completely optional. If your licensed therapist or outpatient clinic uses the Luminous Break Coach Portal, they can issue a secure 6-digit PIN or QR code. Navigate to <strong>Settings → Care Circle Connection → Pair with Therapist</strong> and enter the 6-digit PIN to establish an end-to-end encrypted connection.
              </p>
            </div>

            <div style={{ padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 700, color: '#0F172A' }}>
                Why does the iOS app request Screen Time / Family Controls permissions?
              </h4>
              <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.6', color: '#475569' }}>
                On physical iOS devices, Apple's Screen Time / Family Controls framework provides the system shielding mechanism that temporarily intercepts distracting apps so you can complete your mindful pause (e.g., 16-second box breathing). This runs entirely through Apple's privacy-preserving system extensions; your device usage data is never exported.
              </p>
            </div>
          </div>

          {/* Corporate Headquarters Info */}
          <div style={{
            padding: '20px',
            backgroundColor: '#FAF8F5',
            borderRadius: '12px',
            border: '1px solid #EAE0D5',
            fontSize: '13px',
            color: '#5C4A3E',
          }}>
            <strong style={{ display: 'block', marginBottom: '4px', color: '#2D2826' }}>
              Luminous Break Inc. — Corporate Headquarters
            </strong>
            500 Howard Street, Suite 400<br />
            San Francisco, CA 94105, United States<br />
            General Inquiries: <a href="mailto:support@luminousbreak.com" style={{ color: '#2563EB' }}>support@luminousbreak.com</a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#FFFFFF',
        borderTop: '1px solid #E2E8F0',
        padding: '24px',
        textAlign: 'center',
        fontSize: '12px',
        color: '#64748B',
      }}>
        <p style={{ margin: 0 }}>
          © 2026 Luminous Break Inc. All rights reserved. • 500 Howard Street, Suite 400, San Francisco, CA 94105
        </p>
      </footer>
    </div>
  );
};
